import {
  Box,
  Icons,
  MetricsBox,
  RatioProgressBar,
  Stack,
  tokens,
  Tooltip,
  Typography,
} from '@cast/design-system';

import { FailedToLoad } from '@cast/shared-messages-ui';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { CardCurrentDate } from '../_components/CardCurrentDate';
import { InventoryListItem } from '../../../_components/ui/InventoryListItem/InventoryListItem';
import { useInventoryWorkloadsDashboardNavigation } from '../_hooks/useInventoryWorkloadsDashboardNavigation';
import { RECOMMENDATION_STATUS_FILTERS } from '../_utils/dashboardListNavigation';
import {
  RecommendationStatusCardItem,
  useRecommendationStatusCardData,
} from './_hooks/useRecommendationStatusCardData';
import { DashboardMetricSummaryCardSkeleton } from '../_components/DashboardCardSkeletons';

const CARD_TITLE = 'Recommendation status';

const STATUS_COLOR_MAP: Record<RecommendationStatusCardItem['key'], string> = {
  applied: tokens.theme.data.green500,
  lowConfidence: tokens.theme.data.yellow500,
  failed: tokens.theme.data.red300,
  notAvailable: tokens.theme.data.grey200,
};

const RecommendationStatusCardSkeleton = () => (
  <DashboardMetricSummaryCardSkeleton
    metricTestId="recommendation-status-applied-skeleton"
    listCount={4}
  />
);

const RecommendationStatusBar = ({
  items,
}: {
  items: RecommendationStatusCardItem[];
}) => (
  <RatioProgressBar
    testId="recommendation-status-bar"
    bars={items.map((item) => ({
      value: item.value,
      color: STATUS_COLOR_MAP[item.key],
    }))}
    hideBorders
  />
);

const RecommendationStatusList = ({
  items,
  onItemClick,
  testId,
}: {
  items: RecommendationStatusCardItem[];
  onItemClick: (key: RecommendationStatusCardItem['key']) => void;
  testId?: string;
}) => (
  <Stack
    gap={0}
    flex="0 1 auto"
    minHeight={0}
    overflow="auto"
    data-testid={testId}
  >
    <Typography
      variant="L10M"
      color="textSecondary"
      sx={{ textTransform: 'uppercase', pb: 8 }}
    >
      Application status
    </Typography>
    {items.map((item, index) => {
      const filters = RECOMMENDATION_STATUS_FILTERS[item.key];

      return (
        <InventoryListItem
          key={item.key}
          size="sm"
          divider={index < items.length - 1}
          onClick={filters ? () => onItemClick(item.key) : undefined}
          slots={{
            startContent: (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: tokens.theme.radius.xs,
                  backgroundColor: STATUS_COLOR_MAP[item.key],
                }}
              />
            ),
            label:
              item.key === 'notAvailable' ? (
                <Stack direction="row" gap={4} alignItems="center">
                  <Typography variant="P12R">{item.label}</Typography>
                  <Tooltip
                    title="Workloads without available recommendations."
                    placement="top"
                  >
                    <Icons.Info size={14} />
                  </Tooltip>
                </Stack>
              ) : (
                item.label
              ),
            endContent: item.value,
          }}
          slotProps={{
            endContent: { variant: 'P12B' },
          }}
        />
      );
    })}
  </Stack>
);

export const RecommendationStatusCard = () => {
  const { data, isLoading, isFetching, hasError, refetch } =
    useRecommendationStatusCardData();
  const { navigateToAutoscalingList } =
    useInventoryWorkloadsDashboardNavigation();

  const isCardLoading = isLoading || (hasError && isFetching);

  if (!data && !isCardLoading && !hasError) {
    return null;
  }

  return (
    <WidgetLayout
      title={CARD_TITLE}
      testId="recommendation-status-card"
      headerEndContent={<CardCurrentDate />}
    >
      {isCardLoading ? (
        <RecommendationStatusCardSkeleton />
      ) : hasError ? (
        <FailedToLoad
          title="Failed to load recommendation status"
          refresh={refetch}
        />
      ) : data ? (
        <Stack height="100%" gap={24} justifyContent="space-between">
          <Stack
            gap={24}
            data-testid="recommendation-status-card-summary"
            sx={{ flexShrink: 0 }}
          >
            <MetricsBox
              testId="recommendation-status-applied"
              size="m"
              slots={{
                label: 'Applied',
                value: `${data.appliedPercentage}%`,
              }}
              slotProps={{
                label: { variant: 'L12M', color: 'textSecondary' },
                value: { variant: 'metricmd' },
              }}
            />

            <RecommendationStatusBar items={data.items} />
          </Stack>

          <RecommendationStatusList
            items={data.items}
            testId="recommendation-status-card-details"
            onItemClick={(key) => {
              const filters = RECOMMENDATION_STATUS_FILTERS[key];
              if (filters) {
                navigateToAutoscalingList(filters);
              }
            }}
          />
        </Stack>
      ) : null}
    </WidgetLayout>
  );
};
